/**
 * THE COPILOT'S OPERATIONAL STATES, AND WHAT EACH ONE SAYS.
 *
 * Pure: a phase, a command list and a usage block in, a string out. The component owns the
 * clock and the transitions; this file owns the vocabulary, for the reason every other
 * label module here exists — a sentence written inline in JSX is a second description of
 * something the code already knows, and the copy that goes stale is the one nobody reads.
 *
 * <h2>Why a phase at all, rather than one spinner</h2>
 *
 * A Copilot query is three waits with different causes: the model reading the sentence, the
 * browser applying the commands, and — with voice — the microphone. One spinner over all
 * three makes "the model is slow" and "the chart is slow" the same picture, so a reader
 * cannot tell a provider problem from a rendering one. Naming the phase is what separates
 * them, and the elapsed timer is what makes a long one legible rather than alarming.
 */

/** The states, in the order a voice-driven query passes through them. */
export const PHASE = Object.freeze({
  IDLE: 'idle',
  LISTENING: 'listening',
  INTERPRETING: 'interpreting',
  EXECUTING: 'executing',
  DONE: 'done',
});

/**
 * How the elapsed timer ticks, in ms.
 *
 * Not a frame. The reading is shown to one decimal, so sixty re-renders a second would buy a
 * precision the label cannot display — and this component is portalled over the chart, where
 * every render is a render the chart's own work is competing with.
 */
export const TICK_MS = 100;

/** `0.8s`, `12s`. One decimal while that decimal means something. */
export function formatSeconds(ms) {
  if (!Number.isFinite(ms) || ms < 0) return null;
  const s = ms / 1000;
  return s < 10 ? `${s.toFixed(1)}s` : `${Math.round(s)}s`;
}

/**
 * WHAT IS BEING EXECUTED — the command's own `kind:target`, never prose.
 *
 * `study.run:aspect` is what the brief asks for and it is also the only label that cannot
 * drift: a hand-written "Running the aspect study" is a second name for one command, and when
 * the two disagree the screen is the one that lies. The command envelope is the backend's own
 * `{ id, kind, target, payload }`.
 *
 * <p>A chain names the FIRST and counts the rest, because four kinds spelled out is a label
 * wider than the bar.
 */
export function describeAction(commands) {
  const list = Array.isArray(commands) ? commands.filter(Boolean) : [];
  if (list.length === 0) return null;
  const [first] = list;
  const name = first.target ? `${first.kind}:${first.target}` : String(first.kind || 'command');
  return list.length === 1 ? name : `${name} +${list.length - 1} more`;
}

/**
 * The status line for a phase.
 *
 * @param phase one of {@link PHASE}
 * @param elapsedMs how long the current phase has been running, for the two that wait
 * @param action the `describeAction` string, for EXECUTING
 * @param totalMs the whole round trip, for DONE
 */
export function phaseLabel(phase, { elapsedMs, action, totalMs } = {}) {
  switch (phase) {
    case PHASE.LISTENING:
      return 'Listening / Transcribing…';
    case PHASE.INTERPRETING: {
      const t = formatSeconds(elapsedMs);
      return t ? `Interpreting intent… ${t}` : 'Interpreting intent…';
    }
    case PHASE.EXECUTING:
      return action ? `Executing: ${action}…` : 'Executing…';
    case PHASE.DONE: {
      const t = formatSeconds(totalMs);
      return t ? `Completed in ${t}` : 'Completed';
    }
    default:
      return null;
  }
}

/**
 * `⚡ 420 tokens · 0.8s` — the counter the brief specifies.
 *
 * <p>⚠ RETURNS NULL RATHER THAN A ZERO when there is no usage. The backend sends `usage: null`
 * for a query no model was asked — not configured, or unreachable — and `0 tokens` would read
 * as a free answer rather than as no answer. Same rule the replay's unsized-account tiles
 * follow.
 *
 * <p>The SUM is shown and the split goes in the tooltip: `420 tokens` is a figure a reader can
 * hold against yesterday's, where `in 380 / out 40` is two figures to hold against four.
 */
export function formatUsage(usage, totalMs) {
  const total = Number(usage?.totalTokens);
  if (!Number.isFinite(total)) return null;
  const t = formatSeconds(totalMs);
  return t ? `⚡ ${total} tokens · ${t}` : `⚡ ${total} tokens`;
}

/** The counter's tooltip: the split the line itself deliberately does not carry. */
export function describeUsage(usage, model) {
  if (!usage) return null;
  const parts = [
    `${usage.inputTokens} input`,
    `${usage.outputTokens} output`,
  ];
  if (model) parts.push(model);
  return parts.join(' · ');
}
